/**
 * Simulator Trajectory Controller (EPIC-047.5)
 *
 * Greffe trajectoire CA sur les simulateurs §17.A et §17.B :
 *
 *  - §17.A (simulateur création prospect, `simulateur.html`) :
 *    bouton « Créer ma trajectoire à partir de cette projection »
 *    qui PATCH `/simulator/scenarios/{id}/proposed-trajectory`
 *    avec un calque exact `ca_trajectory` construit depuis les outputs
 *    de simulation, puis redirige vers
 *    `/pilotage-trajectoire-edition.html?fromSimulationScenarioId=...`.
 *    Inactif tant qu'aucun scénario n'est sauvegardé (`<select>` vide).
 *
 *  - §17.B (simulateur ajout d'activité, `simulateur-ajout-activite.html`) :
 *    bouton conditionnel « Mettre à jour ma trajectoire » qui appelle
 *    `POST /simulator/preview-trajectory-update` puis instancie une
 *    modale de confirmation (template inline). La confirmation déclenche
 *    `PUT /me/trajectory`. Le bouton est absent du DOM si
 *    `GET /me/trajectory?year=currentYear` retourne 404.
 *
 *  - Dashboard ME post-bascule (`tableau-de-bord.html`) :
 *    bandeau d'adoption affiché si un scénario actif possède un
 *    `proposedTrajectory` non null. Adopter → `POST /me/trajectory/adopt-from-simulation`.
 *
 * Mode auto-détecté via `data-simulator-trajectory-mode-value` (création
 * | ajout-activite | dashboard-adoption).
 *
 * No HTML built in JS — chaque nœud dynamique est cloné depuis un
 * `<template>` inline dans la page (conventions Notamicro §8).
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "../services/api.js";

const MONTHS_IN_YEAR = 12;

export default class SimulatorTrajectoryController extends BaseController {
  static targets = [
    // §17.A
    "createBtn",
    // §17.B
    "updateBtn",
    "updateModal",
    "updateModalCurrent",
    "updateModalProposed",
    "updateModalDiffTbody",
    "updateConfirm",
    "updateCancel",
    "updateError",
    // Dashboard adoption
    "adoptionBanner",
    "adoptionScenarioTitle",
    "adoptionTotal",
    "adoptionAdopt",
    "adoptionDismiss",
  ];

  async connect() {
    // Mode auto-détecté selon les targets présents — pas de value mode
    // requise (un même controller peut servir les trois surfaces).
    if (this.hasCreateBtnTarget) {
      this.refreshCreateButtonState();
      this._onSelectChange = (e) => {
        const sel = e.target;
        if (sel?.matches?.('[data-scenario-switcher-target="select"]')) {
          this.refreshCreateButtonState();
        }
      };
      document.addEventListener("change", this._onSelectChange);
    }
    if (this.hasUpdateBtnTarget) {
      await this.toggleUpdateButtonVisibility();
    }
    if (this.hasAdoptionBannerTarget) {
      await this.loadAdoptionBanner();
    }
  }

  disconnect() {
    if (this._onSelectChange) {
      document.removeEventListener("change", this._onSelectChange);
    }
  }

  // ---------------------------------------------------------------------------
  // §17.A — Création trajectoire prospect
  // ---------------------------------------------------------------------------

  refreshCreateButtonState() {
    if (!this.hasCreateBtnTarget) return;
    const id = this._activeScenarioIdFromDom();
    this.createBtnTarget.disabled = id === null;
    this.createBtnTarget.title = id === null
      ? "Sauvegardez d'abord ce scénario pour pouvoir le convertir en trajectoire."
      : "";
  }

  async createFromScenario(event) {
    event.preventDefault();
    if (!this.hasCreateBtnTarget) return;
    const scenarioId = this._activeScenarioIdFromDom();
    if (scenarioId === null) return;

    this.createBtnTarget.disabled = true;
    try {
      const scenario = await api.get(`/simulator/scenarios/${encodeURIComponent(scenarioId)}`);
      const payload = this._buildProposedFromOutputs(scenario);
      await api.patch(
        `/simulator/scenarios/${encodeURIComponent(scenarioId)}/proposed-trajectory`,
        payload,
      );
      const url = `/pilotage-trajectoire-edition.html?trajectoryYear=${encodeURIComponent(payload.year)}&fromSimulationScenarioId=${encodeURIComponent(scenarioId)}`;
      window.location.assign(url);
    } catch (error) {
      this.createBtnTarget.disabled = false;
      window.alert(this._messageForError(
        error,
        "Impossible d'enregistrer le calque de trajectoire.",
      ));
    }
  }

  _activeScenarioIdFromDom() {
    const select = document.querySelector('[data-scenario-switcher-target="select"]');
    const value = select?.value ?? "";
    if (value === "" || value === "__manage__") return null;
    return value;
  }

  _buildProposedFromOutputs(scenario) {
    const outputs = scenario?.outputs ?? {};
    const total = Number(outputs.totalRevenueEur ?? outputs.total_revenue_eur ?? 0);
    const inputs = scenario?.inputs ?? {};
    const targetYear = this._inferTargetYear(inputs);
    const monthly = this._uniformMonthly(total);
    return {
      year: targetYear,
      totalTargetHt: Math.round(total * 100) / 100,
      distributionMode: "uniform",
      monthlyDistribution: monthly,
    };
  }

  _inferTargetYear(inputs) {
    const rawStart = inputs?.startDate ?? inputs?.simulationStartDate ?? null;
    if (typeof rawStart === "string" && rawStart.length >= 4) {
      const parsed = parseInt(rawStart.slice(0, 4), 10);
      if (Number.isFinite(parsed) && parsed >= 2020 && parsed <= 2099) return parsed;
    }
    return new Date().getFullYear() + 1;
  }

  _uniformMonthly(total) {
    const base = Math.floor((total / MONTHS_IN_YEAR) * 100) / 100;
    const monthly = new Array(MONTHS_IN_YEAR).fill(base);
    const remainder = Math.round((total - base * MONTHS_IN_YEAR) * 100) / 100;
    monthly[MONTHS_IN_YEAR - 1] = Math.round((base + remainder) * 100) / 100;
    return monthly;
  }

  // ---------------------------------------------------------------------------
  // §17.B — Mise à jour trajectoire ME
  // ---------------------------------------------------------------------------

  async toggleUpdateButtonVisibility() {
    if (!this.hasUpdateBtnTarget) return;
    const year = new Date().getFullYear();
    try {
      await api.get("/me/trajectory", { year: String(year) });
      this.updateBtnTarget.hidden = false;
    } catch (error) {
      if (error instanceof APIError && error.status === 404) {
        this.updateBtnTarget.remove();
        return;
      }
      this.updateBtnTarget.hidden = false;
    }
  }

  async openUpdateModal(event) {
    event.preventDefault();
    const simId = this._currentActivityAdditionScenarioId();
    if (simId === null) {
      window.alert("Sauvegardez d'abord ce scénario d'ajout d'activité.");
      return;
    }
    try {
      const res = await api.post("/simulator/preview-trajectory-update", {
        activityAdditionSimulationId: simId,
      });
      this._renderUpdateModal(res.preview);
      if (this.hasUpdateModalTarget) this.updateModalTarget.showModal();
    } catch (error) {
      window.alert(this._messageForError(
        error,
        "Impossible de calculer l'aperçu de la mise à jour.",
      ));
    }
  }

  closeUpdateModal() {
    if (this.hasUpdateModalTarget && this.updateModalTarget.open) {
      this.updateModalTarget.close();
    }
  }

  async confirmUpdate() {
    if (!this._lastPreview) return;
    const proposed = this._lastPreview.proposedTrajectory;
    if (this.hasUpdateConfirmTarget) this.updateConfirmTarget.disabled = true;
    try {
      await api.put("/me/trajectory", {
        year: proposed.year,
        totalTargetHt: proposed.totalTargetHt,
        distributionMode: proposed.distributionMode,
        monthlyDistribution: proposed.monthlyDistribution,
        breakdownByActivity: proposed.breakdownByActivity ?? null,
      });
      this.closeUpdateModal();
      window.location.assign(`/pilotage-trajectoire.html?year=${encodeURIComponent(proposed.year)}`);
    } catch (error) {
      if (this.hasUpdateErrorTarget) {
        this.updateErrorTarget.textContent = this._messageForError(
          error,
          "Mise à jour impossible.",
        );
        this.updateErrorTarget.hidden = false;
      }
      if (this.hasUpdateConfirmTarget) this.updateConfirmTarget.disabled = false;
    }
  }

  _currentActivityAdditionScenarioId() {
    const select = document.querySelector('[data-scenario-switcher-target="select"]');
    const value = select?.value ?? "";
    if (value === "" || value === "__manage__") return null;
    return value;
  }

  _renderUpdateModal(preview) {
    this._lastPreview = preview;
    if (this.hasUpdateErrorTarget) {
      this.updateErrorTarget.textContent = "";
      this.updateErrorTarget.hidden = true;
    }
    if (this.hasUpdateConfirmTarget) this.updateConfirmTarget.disabled = false;
    if (this.hasUpdateModalCurrentTarget) {
      this.updateModalCurrentTarget.textContent = this._formatEur(preview.currentTrajectory.totalTargetHt);
    }
    if (this.hasUpdateModalProposedTarget) {
      this.updateModalProposedTarget.textContent = this._formatEur(preview.proposedTrajectory.totalTargetHt);
    }
    if (this.hasUpdateModalDiffTbodyTarget) {
      const tbody = this.updateModalDiffTbodyTarget;
      while (tbody.firstChild) tbody.removeChild(tbody.firstChild);
      preview.diff.forEach((row) => {
        const fragment = this.cloneTemplate("tpl-trajectory-update-row");
        this.fillSlots(fragment, {
          month: this._monthLabel(row.month),
          currentEur: this._formatEur(row.currentEur),
          proposedEur: this._formatEur(row.proposedEur),
          deltaEur: this._formatDelta(row.deltaEur),
        });
        tbody.appendChild(fragment);
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Dashboard — bandeau adoption bascule payante
  // ---------------------------------------------------------------------------

  async loadAdoptionBanner() {
    if (!this.hasAdoptionBannerTarget) return;
    try {
      const scenario = await this._findScenarioWithProposedTrajectory();
      if (scenario === null) {
        this.adoptionBannerTarget.hidden = true;
        return;
      }
      this._currentAdoptionScenario = scenario;
      if (this.hasAdoptionScenarioTitleTarget) {
        this.adoptionScenarioTitleTarget.textContent = scenario.title ?? "";
      }
      if (this.hasAdoptionTotalTarget) {
        const total = Number(scenario.proposedTrajectory?.totalTargetHt ?? 0);
        this.adoptionTotalTarget.textContent = this._formatEur(total);
      }
      this.adoptionBannerTarget.hidden = false;
    } catch (_e) {
      this.adoptionBannerTarget.hidden = true;
    }
  }

  async _findScenarioWithProposedTrajectory() {
    const res = await api.get("/simulator/scenarios", { perPage: 100 });
    const items = Array.isArray(res?.data) ? res.data : [];
    return items.find((s) => s.status === "active" && s.proposedTrajectory) ?? null;
  }

  async adoptProposedTrajectory(event) {
    event.preventDefault();
    if (!this._currentAdoptionScenario) return;
    if (this.hasAdoptionAdoptTarget) this.adoptionAdoptTarget.disabled = true;
    try {
      const year = this._currentAdoptionScenario.proposedTrajectory?.year
        ?? new Date().getFullYear();
      await api.post("/me/trajectory/adopt-from-simulation", {
        scenario_id: this._currentAdoptionScenario.id,
        year,
      });
      this.adoptionBannerTarget.hidden = true;
      window.location.assign(`/pilotage-trajectoire.html?year=${encodeURIComponent(year)}`);
    } catch (error) {
      if (this.hasAdoptionAdoptTarget) this.adoptionAdoptTarget.disabled = false;
      window.alert(this._messageForError(
        error,
        "Adoption de la trajectoire impossible.",
      ));
    }
  }

  dismissAdoptionBanner() {
    if (this.hasAdoptionBannerTarget) this.adoptionBannerTarget.hidden = true;
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _formatEur(value) {
    const n = Number(value) || 0;
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(n);
  }

  _formatDelta(value) {
    const n = Number(value) || 0;
    const formatted = this._formatEur(Math.abs(n));
    if (n > 0) return `+${formatted}`;
    if (n < 0) return `−${formatted}`;
    return formatted;
  }

  _monthLabel(monthIndex1) {
    const labels = [
      "Janv.", "Févr.", "Mars", "Avr.", "Mai", "Juin",
      "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc.",
    ];
    const idx = Math.max(1, Math.min(MONTHS_IN_YEAR, Number(monthIndex1) || 1)) - 1;
    return labels[idx];
  }

  _messageForError(error, fallback) {
    if (error instanceof APIError && error.message) return error.message;
    if (error && typeof error.message === "string" && error.message !== "") return error.message;
    return fallback;
  }
}
